import { useState } from 'react';
import { useAppState } from '@/hooks/useAppState';
import { useFinanceRefresh } from '@/hooks/useFinanceRefresh';
import PrivacyBadge from '@/components/feature/PrivacyBadge';

export default function Profile() {
  const {
    darkMode,
    toggleDarkMode,
    toastMessage,
    clearToast,
    currentUser,
    cloudSyncEnabled,
    sendSignInLink,
    signOut,
  } = useAppState();
  const [email, setEmail] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [cardOrder, setCardOrder] = useState(['今日行动', '项目推进', '财经热点速览']);
  const [shareConfirm, setShareConfirm] = useState(true);
  const [notifyFinance, setNotifyFinance] = useState(true);
  const [notifyTasks, setNotifyTasks] = useState(true);

  const {
    autoRefreshEnabled,
    refreshInterval,
    nextUpdateTime,
    setAutoRefreshEnabled,
    setRefreshInterval,
  } = useFinanceRefresh();

  const handleSignIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) return;
    setAuthBusy(true);
    try {
      await sendSignInLink(email.trim());
      toastMessage || clearToast();
      window.alert('登录链接已发送，请打开邮箱确认。');
    } catch {
      window.alert('登录链接发送失败，请检查邮箱或稍后重试。');
    } finally {
      setAuthBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 pb-20">
      {/* Header */}
      <header
        className="px-4 pt-4 pb-4"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      >
        <h1 className="text-lg font-bold text-foreground-800 font-heading">我的</h1>
      </header>

      <main className="px-4 space-y-5">
        {/* Profile card */}
        <section className="bg-background-50 rounded-xl px-4 py-4 border border-background-200/60">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-primary-600 font-semibold text-lg font-heading">李</span>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-foreground-800">李明远</h2>
              <p className="text-xs text-foreground-400 mt-0.5">保持专注，保持好奇</p>
            </div>
            <button className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-edit-line text-sm text-foreground-400" />
            </button>
          </div>
        </section>

        {/* Cloud sync account */}
        <section className="bg-background-50 rounded-xl px-4 py-4 border border-background-200/60">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground-800">云端同步</h2>
              <p className="text-[10px] text-foreground-400 mt-0.5">
                {currentUser ? `已登录：${currentUser.email ?? '当前账号'}` : '登录后同步待办和项目'}
              </p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cloudSyncEnabled ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-600'}`}>
              {cloudSyncEnabled ? '已连接' : '未连接'}
            </span>
          </div>
          {currentUser ? (
            <button
              onClick={() => void signOut()}
              className="w-full py-2 rounded-lg bg-background-100 text-xs text-foreground-600 font-medium cursor-pointer"
            >
              退出登录
            </button>
          ) : (
            <form onSubmit={handleSignIn} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="输入邮箱接收登录链接"
                className="min-w-0 flex-1 px-3 py-2 rounded-lg bg-background-100 text-xs text-foreground-700 outline-none"
              />
              <button
                type="submit"
                disabled={authBusy}
                className="px-3 py-2 rounded-lg bg-primary-500 text-white text-xs font-medium cursor-pointer disabled:opacity-50"
              >
                {authBusy ? '发送中…' : '登录'}
              </button>
            </form>
          )}
        </section>

        {/* Data & Privacy */}
        <section>
          <h2 className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
            数据与隐私
          </h2>
          <div className="bg-background-50 rounded-xl border border-background-200/60 divide-y divide-background-200/50">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center">
                  <i className="ri-lock-line text-secondary-600 text-sm" />
                </span>
                <div>
                  <p className="text-xs font-medium text-foreground-700">私密数据</p>
                  <p className="text-[10px] text-foreground-400">
                    待办、项目、笔记 · 默认仅自己可见
                  </p>
                </div>
              </div>
              <PrivacyBadge type="private" />
            </div>
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-earth-line text-accent-600 text-sm" />
                </span>
                <div>
                  <p className="text-xs font-medium text-foreground-700">财经分享</p>
                  <p className="text-[10px] text-foreground-400">
                    公开热点资讯 · 不包含个人数据
                  </p>
                </div>
              </div>
              <PrivacyBadge type="shareable" />
            </div>
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                  <i className="ri-alert-line text-amber-500 text-sm" />
                </span>
                <div>
                  <p className="text-xs font-medium text-foreground-700">分享前确认</p>
                  <p className="text-[10px] text-foreground-400">
                    分享前弹窗确认，避免误操作
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShareConfirm(!shareConfirm)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  shareConfirm ? 'bg-accent-500' : 'bg-background-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                    shareConfirm ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Finance Update Settings - NEW */}
        <section>
          <h2 className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
            财经更新
          </h2>
          <div className="bg-background-50 rounded-xl border border-background-200/60 divide-y divide-background-200/50">
            {/* Auto refresh toggle */}
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-refresh-line text-accent-500 text-sm" />
                </span>
                <div>
                  <p className="text-xs font-medium text-foreground-700">自动更新</p>
                  <p className="text-[10px] text-foreground-400">
                    {autoRefreshEnabled ? '自动更新已开启' : '仅手动更新'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  autoRefreshEnabled ? 'bg-accent-500' : 'bg-background-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                    autoRefreshEnabled ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Refresh interval */}
            {autoRefreshEnabled && (
              <div className="px-4 py-3">
                <p className="text-xs font-medium text-foreground-700 mb-2">更新频率</p>
                <div className="flex items-center gap-2">
                  {[15, 30, 60].map((interval) => (
                    <button
                      key={interval}
                      onClick={() => setRefreshInterval(interval)}
                      className={`text-[11px] px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                        refreshInterval === interval
                          ? 'bg-foreground-800 text-background-50'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      每 {interval} 分钟
                    </button>
                  ))}
                </div>
                {nextUpdateTime && (
                  <p className="text-[10px] text-foreground-400 mt-2">
                    下次预计更新：{nextUpdateTime}
                  </p>
                )}
              </div>
            )}

            {/* Disclaimer */}
            <div className="px-4 py-3">
              <p className="text-[10px] text-foreground-400 leading-relaxed">
                自动更新将在正式接入财经数据源后启用；当前仅为原型演示。
              </p>
            </div>
          </div>
        </section>

        {/* Preferences */}
        <section>
          <h2 className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
            工作台偏好
          </h2>
          <div className="bg-background-50 rounded-xl border border-background-200/60 divide-y divide-background-200/50">
            {/* Card order */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center">
                    <i className="ri-stack-line text-primary-500 text-sm" />
                  </span>
                  <p className="text-xs font-medium text-foreground-700">首页卡片排序</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {cardOrder.map((card, i) => (
                  <span
                    key={card}
                    className="text-[10px] px-2 py-1 rounded-full bg-background-100 text-foreground-500 font-medium whitespace-nowrap"
                  >
                    {i + 1}. {card}
                  </span>
                ))}
              </div>
            </div>

            {/* Default finance topic */}
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-accent-500 text-sm" />
                </span>
                <p className="text-xs font-medium text-foreground-700">默认财经主题</p>
              </div>
              <span className="text-[10px] text-foreground-400">宏观政策 · 科技</span>
            </div>

            {/* Dark mode */}
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-foreground-100 flex items-center justify-center">
                  <i className="ri-moon-line text-foreground-600 text-sm" />
                </span>
                <p className="text-xs font-medium text-foreground-700">深色模式</p>
              </div>
              <button
                onClick={toggleDarkMode}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  darkMode ? 'bg-primary-500' : 'bg-background-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                    darkMode ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Notification */}
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                  <i className="ri-notification-3-line text-amber-500 text-sm" />
                </span>
                <p className="text-xs font-medium text-foreground-700">财经热点通知</p>
              </div>
              <button
                onClick={() => setNotifyFinance(!notifyFinance)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  notifyFinance ? 'bg-accent-500' : 'bg-background-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                    notifyFinance ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                  <i className="ri-task-line text-blue-500 text-sm" />
                </span>
                <p className="text-xs font-medium text-foreground-700">待办提醒</p>
              </div>
              <button
                onClick={() => setNotifyTasks(!notifyTasks)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  notifyTasks ? 'bg-accent-500' : 'bg-background-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                    notifyTasks ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Data Management */}
        <section>
          <h2 className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
            数据管理
          </h2>
          <div className="bg-background-50 rounded-xl border border-background-200/60 divide-y divide-background-200/50">
            <button className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-background-100 transition-colors cursor-pointer">
              <span className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center">
                <i className="ri-download-2-line text-secondary-600 text-sm" />
              </span>
              <span className="text-xs font-medium text-foreground-700">导出数据</span>
            </button>
            <div className="px-4 py-3 flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center">
                <i className="ri-cloud-line text-secondary-600 text-sm" />
              </span>
              <div className="flex-1">
                <p className="text-xs font-medium text-foreground-700">实时同步</p>
                <p className="text-[10px] text-foreground-400">
                  已接入 Supabase；登录后同步待办和项目
                </p>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                已接入
              </span>
            </div>
          </div>
        </section>

        {/* About */}
        <section className="pb-6 text-center">
          <p className="text-[10px] text-foreground-300">
            个人工作台 · MVP · 财经数据源配置后自动更新
          </p>
        </section>
      </main>

      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className="px-4 py-2 rounded-lg bg-foreground-800 text-background-50 text-xs font-medium shadow-lg flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-check-line text-sm" />
            </span>
            {toastMessage}
          </div>
        </div>
      )}
    </div>
  );
}
